import api, { initialiseCsrf } from "../api/httpClient";
import { API_PATHS } from "../api/apiPaths";

const extractPayload = (response) => response?.data?.data ?? response?.data ?? response ?? {};
const extractList = (response) => {
  const payload = extractPayload(response);
  if (Array.isArray(payload)) return payload;
  return payload.activities || payload.content || payload.items || [];
};

const validateResponse = (response, fallback) => {
  if (response?.data?.success === false) throw new Error(response.data.message || fallback);
  return extractPayload(response);
};

const DevActivityService = {
  async getActivities(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => { if (value && value !== "all") query.set(key, value); });
    const suffix = query.toString();
    const response = await api.get(`${API_PATHS.superAdmin.devActivities()}${suffix ? `?${suffix}` : ""}`);
    if (response?.data?.success === false) throw new Error(response.data.message || "Unable to load activities.");
    return extractList(response);
  },

  async getActivity(activityId) {
    try {
      return validateResponse(
        await api.get(API_PATHS.superAdmin.devActivityById(activityId)),
        "Unable to load activity.",
      );
    } catch (detailError) {
      // Keep details usable when the backend's single-activity endpoint is
      // unavailable but the collection endpoint already contains the record.
      try {
        const activities = await this.getActivities();
        const match = activities.find((item) =>
          String(item?.id || item?.activityId || item?._id || "") === String(activityId)
        );
        if (match) return match;
      } catch {
        // Preserve the more useful error from the requested detail endpoint.
      }
      throw detailError;
    }
  },

  async createActivity(payload) {
    await initialiseCsrf();
    return validateResponse(await api.post(API_PATHS.superAdmin.devActivities(), payload), "Unable to create activity.");
  },

  async updateActivity(activityId, payload) {
    await initialiseCsrf();
    return validateResponse(await api.patch(API_PATHS.superAdmin.devActivityById(activityId), payload), "Unable to update activity.");
  },

  async deleteActivity(activityId) {
    await initialiseCsrf();
    await validateResponse(await api.delete(API_PATHS.superAdmin.devActivityById(activityId)), "Unable to delete activity.");
  },

  async getComments(activityId) {
    const response = await api.get(API_PATHS.superAdmin.devActivityComments(activityId));
    if (response?.data?.success === false) {
      throw new Error(response.data.message || "Unable to load comments.");
    }
    return extractList(response);
  },

  async addComment(activityId, { body, parentCommentId = null }) {
    await initialiseCsrf();
    const payload = {
      body: String(body || "").trim(),
      parentCommentId: parentCommentId || null,
    };
    return validateResponse(
      await api.post(API_PATHS.superAdmin.devActivityComments(activityId), payload),
      "Unable to post comment."
    );
  },
};

export default DevActivityService;
